/**
 * Playbook de vendas: a caixinha flutuante que abre nas propostas (consórcio
 * e seguro) e na ficha do contato/lead (prospecção). Quem pensa é a North IA
 * (`chat-ai`, modoCoach "playbook" — ver db/supabase/functions/chat-ai/
 * playbook.ts): daqui só vai {tipo, id}; os dados são lidos lá, com a RLS.
 *
 * Cada proposta/contato tem UMA conversa, lembrada neste navegador: reabrir
 * continua de onde parou. A conversa também aparece em North IA → Conversas,
 * como "Playbook · Nome".
 */

import { sendChatMessage, type ChatAiResponse } from "@/lib/chat-ai-client";

export type TipoPlaybook = "consorcio" | "seguro" | "contato";

export interface AlvoPlaybook {
  tipo: TipoPlaybook;
  /** uuid da proposal_simulations, id da cotação de seguro ou do contato. */
  id: string | number;
  /** Só para o cabeçalho; o servidor não usa. */
  nome?: string | null;
}

/** Tem que ser igual a PEDIDO_INICIAL_PLAYBOOK do chat-ai/playbookPrompt.ts. */
export const PEDIDO_INICIAL = "Monte o playbook para esta conversa.";

export const TITULO: Record<TipoPlaybook, string> = {
  consorcio: "Playbook · Consórcio",
  seguro: "Playbook · Seguro de vida",
  contato: "Prospecção",
};

/** Atalhos do que o cliente costuma dizer em cada momento. */
export const FALAS_RAPIDAS: Record<TipoPlaybook, string[]> = {
  consorcio: [
    "Achou a parcela alta",
    "Prefere financiar",
    "Vai pensar",
    "Não quer esperar a contemplação",
  ],
  seguro: [
    "Achou caro",
    "Não assinou ainda",
    "Já tem seguro no banco",
    "Vai falar com a família",
  ],
  contato: [
    "Disse que não tem interesse agora",
    "Pediu para mandar por WhatsApp",
    "Perguntou quanto custa",
    "Quer trocar de carro",
  ],
};

export const chaveConversa = (alvo: AlvoPlaybook) => `north.playbook.${alvo.tipo}.${alvo.id}`;

/** localStorage pode não existir ou lançar (aba privada, bloqueio): sem ele, só não lembra. */
export const lembrar = {
  ler(alvo: AlvoPlaybook): string | undefined {
    try {
      return localStorage.getItem(chaveConversa(alvo)) ?? undefined;
    } catch {
      return undefined;
    }
  },
  gravar(alvo: AlvoPlaybook, conversationId: string) {
    try {
      localStorage.setItem(chaveConversa(alvo), conversationId);
    } catch {
      // sem memória local: a próxima abertura começa conversa nova
    }
  },
  esquecer(alvo: AlvoPlaybook) {
    try {
      localStorage.removeItem(chaveConversa(alvo));
    } catch {
      // idem
    }
  },
};

/** A conversa lembrada pode ter saído no limite de 30 (migration 205) — aí começa outra. */
const CONVERSA_SUMIU = /conversa nao encontrada/i;

export async function enviarNoPlaybook(
  alvo: AlvoPlaybook,
  mensagem: string,
  conversationId: string | undefined,
  enviar: typeof sendChatMessage = sendChatMessage,
): Promise<ChatAiResponse> {
  const pedido = { message: mensagem, modoCoach: "playbook" as const, playbook: { tipo: alvo.tipo, id: alvo.id } };
  try {
    return await enviar({ ...pedido, conversationId });
  } catch (e) {
    if (!conversationId || !CONVERSA_SUMIU.test(String((e as Error)?.message))) throw e;
    lembrar.esquecer(alvo);
    return enviar(pedido);
  }
}

/** Mensagem do vendedor a partir de um atalho: deixa claro que é fala do cliente. */
export const falaDoCliente = (atalho: string) => `O cliente: ${atalho.charAt(0).toLowerCase()}${atalho.slice(1)}.`;
